import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LEDGER_KIND_LABELS, type LedgerKind } from '@shared/domain';
import { formatINR } from '@shared/money';
import { api, actionKey } from '../../api';
import type { LedgerEntry, OpenItem, Receipt, RelSummary } from '../../types';
import { Badge, ConfirmDialog, Empty, ErrorBox, Field, Loading, RupeeInput, day, dt, inr, rupeesToPaise, statusTone, today, useBusy } from '../../ui';
import { openAttachment } from '../buyer/Indents';

export function SupplierPayments() {
  const [status, setStatus] = useState('PENDING_CONFIRMATION');
  const q = useQuery({ queryKey: ['payments', status], queryFn: () => api.get<Receipt[]>(`/api/payments${status ? `?status=${status}` : ''}`) });
  const dash = useQuery({ queryKey: ['supplier-dash'], queryFn: () => api.get<{ customers: RelSummary[] }>('/api/supplier/dashboard') });
  const name = (id: string) => dash.data?.customers.find((c) => c.relationshipId === id)?.counterpartyName ?? '';
  return (
    <div className="card">
      <h1>Payments</h1>
      <p className="muted">Check your bank statement, then confirm. Credit returns to the customer only after you confirm. To record money that arrived without a report, open the customer.</p>
      <div className="row">
        {[['PENDING_CONFIRMATION', 'To check'], ['CONFIRMED', 'Confirmed'], ['', 'All']].map(([v, l]) => <button key={l} className={`btn small ${status === v ? 'primary' : ''}`} onClick={() => setStatus(v)}>{l}</button>)}
      </div>
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      {q.data && !q.data.length ? <Empty>Nothing here.</Empty> : null}
      <ul className="list">
        {q.data?.map((r) => (
          <li key={r._id}>
            <Link className="item" to={`/supplier/payments/${r._id}`}>
              <div className="row between">
                <strong>{name(r.relationshipId)} · {inr(r.amountPaise)}</strong>
                <Badge tone={statusTone(r.status)}>{r.status.replace(/_/g, ' ').toLowerCase()}</Badge>
              </div>
              <div className="row between"><span className="mono">{r.reference}</span><small>paid {day(r.paidOn)} · {r.source === 'BUYER_REPORTED' ? 'reported by customer' : 'recorded by you'}</small></div>
              {r.possibleDuplicateOf.length ? <Badge tone="warn">possible duplicate</Badge> : null}
              {r.status === 'CONFIRMED' && BigInt(r.amountPaise) - BigInt(r.allocatedPaise) - BigInt(r.refundedPaise) > 0n ? <Badge tone="warn">not fully applied</Badge> : null}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface PaymentData {
  receipt: Receipt;
  entries: LedgerEntry[];
  refunds: { _id: string; amountPaise: string; paidOn: string; reference: string; reason: string }[];
  suggestion: { itemId: string; amountPaise: string }[];
  unallocatedPaise: string;
}

function oldestFirst(items: OpenItem[], amount: string): Record<string, string> {
  let left = BigInt(amount);
  const plan: Record<string, string> = {};
  for (const item of items) {
    if (left <= 0n) break;
    const due = BigInt(item.unpaidPaise);
    const use = due < left ? due : left;
    plan[item.itemId] = formatINR(use, { symbol: false }).replace(/,/g, '');
    left -= use;
  }
  return plan;
}

export function PaymentDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['payment', id], queryFn: () => api.get<PaymentData>(`/api/payments/${id}`) });
  const relId = q.data?.receipt.relationshipId;
  const ledger = useQuery({ queryKey: ['ledger', relId], queryFn: () => api.get<{ openItems: OpenItem[] }>(`/api/relationships/${relId}/ledger`), enabled: Boolean(relId) });
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [allocationRetry, setAllocationRetry] = useState(false);
  const pendingAllocation = useRef<{ allocations: { itemId: string; amountPaise: string }[]; key: ReturnType<typeof actionKey> } | null>(null);
  const [dlg, setDlg] = useState<'confirm' | 'reject' | 'reverse' | 'refund' | 'allocate' | { undo: LedgerEntry } | null>(null);
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState({ amount: '', date: today(), reference: '' });
  const { busy, error, run, setError } = useBusy();

  useEffect(() => {
    if (q.data?.receipt.status === 'PENDING_CONFIRMATION') setAlloc(Object.fromEntries(q.data.suggestion.map((s) => [s.itemId, formatINR(s.amountPaise, { symbol: false }).replace(/,/g, '')])));
  }, [q.data?.suggestion]);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const { receipt: r, entries, refunds, unallocatedPaise } = q.data!;
  const items = ledger.data?.openItems ?? [];
  const allocations = items.map(i => ({ itemId: i.itemId, amountPaise: rupeesToPaise(alloc[i.itemId] ?? '') })).filter((a) => a.amountPaise && a.amountPaise !== '0') as { itemId: string; amountPaise: string }[];
  const allocTotal = allocations.reduce((s, a) => s + BigInt(a.amountPaise), 0n);
  const available = r.status === 'CONFIRMED' ? unallocatedPaise : r.amountPaise;
  const invalidAmount = items.some(i => (alloc[i.itemId] ?? '').trim() && rupeesToPaise(alloc[i.itemId]) === null);
  const overBill = items.some(i => BigInt(rupeesToPaise(alloc[i.itemId] ?? '') ?? '0') > BigInt(i.unpaidPaise));
  const allocationProblem = invalidAmount ? 'Enter a valid amount with at most two decimal places.' : overBill ? 'An amount exceeds the balance still due on its bill.' : allocTotal > BigInt(available) ? 'The total exceeds the amount available on this payment.' : null;
  const allocationReady = ledger.isSuccess && !ledger.isFetching && !q.isFetching && !allocationProblem;
  const close = () => { setDlg(null); setReason(''); setError(null); };
  const after = async () => { close(); await qc.invalidateQueries(); };
  const refreshAllocation = async () => {
    const [payment, bills] = await Promise.all([q.refetch(), ledger.refetch()]);
    if (payment.isSuccess && bills.isSuccess) setAlloc(oldestFirst(bills.data.openItems, payment.data.unallocatedPaise));
  };
  const applyAllocation = async () => {
    if (!pendingAllocation.current) {
      if (!allocationReady || allocTotal <= 0n) return;
      pendingAllocation.current = { allocations, key: actionKey(`allocate:${r._id}`) };
    }
    const pending = pendingAllocation.current;
    try {
      await api.post(`/api/payments/${r._id}/allocate`, { allocations: pending.allocations }, { idempotencyKey: pending.key.key });
      pending.key.done(); pendingAllocation.current = null; setAllocationRetry(false); setAlloc({});
      await after();
    } catch (err) {
      const status = (err as { status?: number }).status;
      const definitive = Boolean(status && status < 500 && (err as { code?: string }).code !== 'REQUEST_IN_PROGRESS');
      if (definitive) { pending.key.done(); pendingAllocation.current = null; }
      setAllocationRetry(!definitive);
      throw err;
    }
  };
  const dup = r.possibleDuplicateOf.length > 0 || (error as { code?: string } | null)?.code === 'POSSIBLE_DUPLICATE';
  const allocs = entries.filter((e) => e.kind === 'PAYMENT_ALLOCATION');
  const reversed = new Set(entries.filter((e) => e.kind === 'ALLOCATION_REVERSAL').map((e) => (e as unknown as { reversesEntryId: string }).reversesEntryId));

  const AllocTable = ledger.isPending || ledger.isFetching ? <p role="status">Loading unpaid bills…</p> : ledger.isError ? <ErrorBox error={ledger.error} onRetry={() => ledger.refetch()} /> : !items.length ? (
    <div className="alert info">
      <strong>No unpaid bills for this customer.</strong>
      <p>{inr(available)} has no bill to apply to right now. Keep it on this payment for a future bill, or record a refund after returning the money to the customer.</p>
      {r.status === 'PENDING_CONFIRMATION' ? <p>You can still confirm receipt of the money. It will remain unapplied.</p> : null}
      <p>This amount does not increase the customer's credit limit.</p>
    </div>
  ) : (
    <div className="stack">
      <p>Enter how much to apply against each unpaid bill, or use the oldest bills first. You can edit the suggested amounts.</p>
      <button className="btn small" disabled={busy || allocationRetry} onClick={() => setAlloc(oldestFirst(items, available))}>Use oldest bills first</button>
      <div className="table-wrap">
      <table>
        <thead><tr><th>Bill</th><th>Due</th><th className="num">Still due</th><th className="num">Apply (₹)</th></tr></thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.itemId}>
              <td>{LEDGER_KIND_LABELS[i.kind as LedgerKind]}<div><small>{i.description}</small></div></td>
              <td>{day(i.dueDate)}</td>
              <td className="num">{inr(i.unpaidPaise)}</td>
              <td className="num" style={{ width: 140 }}><RupeeInput ariaLabel={`Apply to ${i.description} (₹)`} disabled={busy || allocationRetry} value={alloc[i.itemId] ?? ''} onChange={(v) => setAlloc({ ...alloc, [i.itemId]: v })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <p>Applying {formatINR(allocTotal)} of {inr(available)}. Any remainder stays on this payment for a future bill or refund; it does not increase the customer's credit limit.</p>
      {allocationProblem ? <p className="alert bad" role="alert">{allocationProblem}</p> : null}
    </div>
  );

  return (
    <div className="stack">
      <div className="card">
        <div className="row between">
          <h1>{inr(r.amountPaise)} · {r.reference}</h1>
          <Badge tone={statusTone(r.status)}>{r.status.replace(/_/g, ' ').toLowerCase()}</Badge>
        </div>
        <div className="summary">
          <dl>
            <dt>Paid on</dt><dd>{day(r.paidOn)} · {r.method.replace('_', ' ').toLowerCase()}</dd>
            <dt>{r.source === 'BUYER_REPORTED' ? 'Reported by' : 'Recorded by'}</dt><dd>{r.reportedBy.name} · {dt(r.reportedAt)}</dd>
            {r.confirmedBy ? <><dt>Confirmed by</dt><dd>{r.confirmedBy.name} · {dt(r.confirmedAt)}</dd></> : null}
            <dt>Applied to bills</dt><dd>{inr(r.allocatedPaise)}</dd>
            {BigInt(r.refundedPaise) > 0n ? <><dt>Refunded</dt><dd>{inr(r.refundedPaise)}</dd></> : null}
            {r.status === 'CONFIRMED' ? <><dt>Not applied</dt><dd>{inr(unallocatedPaise)}</dd></> : null}
          </dl>
        </div>
        {r.proofAttachmentId ? <button className="btn small" onClick={() => void openAttachment(r.proofAttachmentId!)}>See proof</button> : null}
        {r.possibleDuplicateOf.length ? <div className="alert warn">This looks like another payment already recorded (same reference or same amount within a day). Check your bank before confirming.</div> : null}
      </div>

      {r.status === 'PENDING_CONFIRMATION' ? (
        <div className="card">
          <h2>Confirm money received</h2>
          <p className="muted">Oldest bills first is suggested. You can change how it is applied.</p>
          {AllocTable}
          <div className="row">
            <button className="btn primary" disabled={!allocationReady} onClick={() => setDlg('confirm')}>Confirm received</button>
            <button className="btn" onClick={() => setDlg('reject')}>Not received</button>
          </div>
        </div>
      ) : null}

      {r.status === 'CONFIRMED' ? (
        <div className="card">
          <h2>Where this payment was applied</h2>
          <ul className="list">
            {allocs.map((a) => (
              <li key={a._id} className="row between">
                <span>{inr(a.amountPaise)} · {a.description} {reversed.has(a._id) ? <Badge>undone</Badge> : null}</span>
                {!reversed.has(a._id) ? <button className="btn small" onClick={() => setDlg({ undo: a })}>Undo</button> : null}
              </li>
            ))}
          </ul>
          <div className="row">
            {BigInt(unallocatedPaise) > 0n || allocationRetry ? <button className="btn" onClick={() => { setDlg('allocate'); setError(null); if (!allocationRetry) { setAlloc({}); void refreshAllocation(); } }}>{allocationRetry ? 'Retry applying payment' : `Apply remaining ${inr(unallocatedPaise)}`}</button> : null}
            {BigInt(unallocatedPaise) > 0n ? <button className="btn" onClick={() => setDlg('refund')}>Record refund of extra</button> : null}
            <button className="btn danger" onClick={() => setDlg('reverse')}>Reverse payment</button>
          </div>
          {refunds.length ? <><h3>Refunds</h3><ul className="list">{refunds.map((f) => <li key={f._id}>{inr(f.amountPaise)} · {day(f.paidOn)} · {f.reference} · {f.reason}</li>)}</ul></> : null}
        </div>
      ) : null}

      <ConfirmDialog open={dlg === 'confirm'} title="Confirm this payment reached your bank?" lines={[['Amount', inr(r.amountPaise)], ['Reference', r.reference], ['Apply to bills', formatINR(allocTotal)], ['Left unapplied', formatINR(BigInt(r.amountPaise) - allocTotal)]]} confirmLabel="Yes, money received" busy={busy} confirmDisabled={!allocationReady} error={error} onClose={close} onConfirm={() => void run(async () => { const k = actionKey(`confirm:${r._id}`); await api.post(`/api/payments/${r._id}/confirm`, { allocations, duplicateReviewNote: reason || undefined }, { idempotencyKey: k.key }); k.done(); await after(); })}>
        {dup ? <Field label="I checked the bank statement (required)" hint="Say why this is not a duplicate"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field> : null}
      </ConfirmDialog>
      <ConfirmDialog open={dlg === 'reject'} title="Payment not received?" lines={[['Amount', inr(r.amountPaise)], ['Reference', r.reference]]} confirmLabel="Mark not received" danger busy={busy} error={error} onClose={close} onConfirm={() => void run(async () => { await api.post(`/api/payments/${r._id}/reject`, { reason }); await after(); })}>
        <Field label="Reason (customer will see this)"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
      <ConfirmDialog open={dlg === 'reverse'} title="Reverse this confirmed payment?" lines={[['Amount', inr(r.amountPaise)], ['Applied', inr(r.allocatedPaise)]]} confirmLabel="Reverse" danger busy={busy} error={error} onClose={close} onConfirm={() => void run(async () => { const k = actionKey(`reverse:${r._id}`); await api.post(`/api/payments/${r._id}/reverse`, { reason }, { idempotencyKey: k.key }); k.done(); await after(); })}>
        <p className="muted">For a mistake such as a bounced cheque. The bills become due again and the customer is told.</p>
        <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
      <ConfirmDialog open={dlg === 'allocate'} title="Apply the remaining amount" lines={[['Not applied', inr(unallocatedPaise)]]} confirmLabel={allocationRetry ? 'Retry apply' : 'Apply'} busy={busy} confirmDisabled={!allocationRetry && (!allocationReady || allocTotal <= 0n)} error={error} onClose={close} onConfirm={() => void run(applyAllocation)}>
        {AllocTable}
        {allocationRetry ? <p>The response was lost. Retry to check the result of the same allocation.</p> : <button className="btn small" disabled={busy || ledger.isFetching || q.isFetching} onClick={() => void refreshAllocation()}>Refresh unpaid bills</button>}
      </ConfirmDialog>
      {dlg && typeof dlg === 'object' ? (
        <ConfirmDialog open title="Undo this allocation?" lines={[['Amount', inr(dlg.undo.amountPaise)]]} confirmLabel="Undo" busy={busy} error={error} onClose={close} onConfirm={() => void run(async () => { const k = actionKey(`undo:${dlg.undo._id}`); await api.post(`/api/payments/${r._id}/deallocate`, { allocationEntryId: dlg.undo._id, reason }, { idempotencyKey: k.key }); k.done(); await after(); })}>
          <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        </ConfirmDialog>
      ) : null}
      <ConfirmDialog open={dlg === 'refund'} title="Record a refund of extra money" lines={[['Not applied', inr(unallocatedPaise)], ['Refund', inr(rupeesToPaise(refund.amount))]]} confirmLabel="Record refund" busy={busy} error={error} onClose={close} onConfirm={() => void run(async () => { const k = actionKey(`refund:${r._id}:${refund.reference}`); await api.post(`/api/payments/${r._id}/refund`, { amountPaise: rupeesToPaise(refund.amount), paidOn: refund.date, reference: refund.reference, reason }, { idempotencyKey: k.key }); k.done(); await after(); })}>
        <p>Return the money through your bank/UPI/cash process first. This records the refund; SuperIndent does not transfer money.</p>
        <Field label="Amount refunded (₹)"><RupeeInput value={refund.amount} onChange={(v) => setRefund({ ...refund, amount: v })} /></Field>
        <Field label="Refund date"><input type="date" value={refund.date} onChange={(e) => setRefund({ ...refund, date: e.target.value })} /></Field>
        <Field label="Refund reference"><input value={refund.reference} onChange={(e) => setRefund({ ...refund, reference: e.target.value })} /></Field>
        <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
    </div>
  );
}
